import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, FileText, Clock, ChevronDown, CheckCircle2, Circle } from "lucide-react";
import { StatusBadge } from "./StatusBadge";
import { UnreadDot } from "./UnreadDot";
import { computeRetention } from "../../lib/retention";

/**
 * Outgoing Enquiry row card.
 *
 * Initial state (compact): Title · Date · "X of N Responded" · retention pill · ▼ expand icon.
 * Tap the card body → navigate to the dedicated Enquiry Details page.
 * Tap the ▼ → inline expand showing every destination business + per-business quotation preview.
 *
 * Expected `enquiry` fields:
 *   { id, title, business_name, enquiry_id_label, date, status, product_summary,
 *     created_at, last_response_at, responded_count, total_suppliers, items,
 *     supplier_ids, supplier_names }
 */
export function EnquiryCard({ enquiry, to, testid }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openBiz, setOpenBiz] = useState(null); // id of expanded business
  const dateStr = enquiry.date
    ? new Date(enquiry.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })
    : "";
  const retention = computeRetention({
    status: enquiry.status,
    created_at: enquiry.created_at,
    last_response_at: enquiry.last_response_at,
  });
  const showRetention = retention !== null && enquiry.status !== "closed" && enquiry.status !== "converted";
  const retentionTone = retention && retention.daysLeft <= 7 ? "text-amber-700 bg-amber-50" : "text-slate-500 bg-slate-50";

  const displayTitle = (enquiry.title && enquiry.title.trim()) || enquiry.business_name || "Untitled enquiry";
  const responded = enquiry.responded_count || 0;
  const total = enquiry.total_suppliers || 0;
  const summaryTone =
    total > 0 && responded === total
      ? "bg-emerald-100 text-emerald-800"
      : responded > 0
        ? "bg-indigo-100 text-indigo-800"
        : "bg-slate-100 text-slate-600";

  // Precompute per-business status + quotes for the inline expand preview
  const perBusiness = (enquiry.supplier_ids || []).map((sid) => {
    const name = (enquiry.supplier_names || {})[sid] || "Business";
    const itemsForBiz = (enquiry.items || []).filter((it) =>
      (it.supplier_ids || [it.supplier_id]).includes(sid)
    );
    const quotedItems = itemsForBiz.filter((it) => (it.quotes || []).some((q) => q.supplier_id === sid));
    let bizStatus = "pending";
    if (itemsForBiz.length > 0 && quotedItems.length === itemsForBiz.length) bizStatus = "responded";
    else if (quotedItems.length > 0) bizStatus = "partial";
    const quotesForBiz = itemsForBiz.map((it) => {
      const qt = (it.quotes || []).find((q) => q.supplier_id === sid) || null;
      return { item: it, quote: qt };
    });
    return { sid, name, bizStatus, quotesForBiz };
  });

  const handleCardTap = (e) => {
    e.preventDefault();
    if (to) navigate(to);
  };
  const toggleExpand = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen((v) => !v);
  };

  return (
    <div
      data-testid={testid || `enquiry-card-${enquiry.id}`}
      className="bg-white border border-slate-100 rounded-2xl transition-all duration-200 hover:shadow-md hover:border-slate-200"
    >
      {/* Compact tappable header */}
      <button
        onClick={handleCardTap}
        data-testid={`enquiry-card-tap-${enquiry.id}`}
        className="w-full text-left p-4 flex items-start gap-3 active:scale-[0.99] transition-transform"
      >
        <div className="w-11 h-11 rounded-2xl bg-indigo-50 text-indigo-600 grid place-items-center shrink-0">
          <FileText className="w-5 h-5" strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1 flex items-center gap-1.5">
              <UnreadDot entityId={enquiry.id} />
              <div className="font-bold text-slate-900 truncate" data-testid={`enquiry-title-${enquiry.id}`}>
                {displayTitle}
              </div>
            </div>
            <StatusBadge status={enquiry.status} />
          </div>
          <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1.5 flex-wrap">
            {dateStr && <span>{dateStr}</span>}
            <span
              data-testid={`enquiry-response-summary-${enquiry.id}`}
              className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold ${summaryTone}`}
            >
              {responded} of {total} Responded
            </span>
            {showRetention && (
              <span
                data-testid={`enquiry-days-left-${enquiry.id}`}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${retentionTone}`}
              >
                <Clock className="w-2.5 h-2.5" strokeWidth={2.4} />
                {retention.daysLeft} {retention.daysLeft === 1 ? "Day" : "Days"} Left
              </span>
            )}
          </div>
        </div>
        <div
          onClick={toggleExpand}
          data-testid={`enquiry-expand-${enquiry.id}`}
          role="button"
          tabIndex={0}
          aria-label={open ? "Collapse preview" : "Expand preview"}
          aria-expanded={open}
          className="w-9 h-9 rounded-full grid place-items-center bg-slate-50 hover:bg-slate-100 shrink-0 self-center"
        >
          <ChevronDown
            className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {/* Inline preview — every destination business with its quotation */}
      {open && (
        <div
          data-testid={`enquiry-preview-${enquiry.id}`}
          className="border-t border-slate-100 divide-y divide-slate-100"
        >
          {perBusiness.length === 0 ? (
            <div className="px-4 py-3 text-xs text-slate-500">No businesses attached.</div>
          ) : (
            perBusiness.map(({ sid, name, bizStatus, quotesForBiz }) => (
              <BusinessPreviewRow
                key={sid}
                name={name}
                bizStatus={bizStatus}
                quotesForBiz={quotesForBiz}
                open={openBiz === sid}
                onToggle={(e) => {
                  e.stopPropagation();
                  setOpenBiz((cur) => (cur === sid ? null : sid));
                }}
                testid={`enquiry-preview-biz-${enquiry.id}-${sid}`}
              />
            ))
          )}
          {to && (
            <button
              onClick={handleCardTap}
              data-testid={`enquiry-open-full-${enquiry.id}`}
              className="w-full flex items-center justify-center gap-1 px-4 py-2.5 text-xs font-bold text-indigo-700 hover:bg-indigo-50 transition"
            >
              Open full enquiry <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function BusinessPreviewRow({ name, bizStatus, quotesForBiz, open, onToggle, testid }) {
  const statusDot =
    bizStatus === "responded"
      ? "bg-emerald-500"
      : bizStatus === "partial"
        ? "bg-amber-400"
        : "bg-slate-300";
  const statusLabel =
    bizStatus === "responded" ? "Responded" : bizStatus === "partial" ? "Partially Responded" : "Response Pending";
  const statusText =
    bizStatus === "responded" ? "text-emerald-700" : bizStatus === "partial" ? "text-amber-700" : "text-slate-500";
  return (
    <div data-testid={testid}>
      <button
        onClick={onToggle}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-slate-50 transition text-left"
      >
        <span className={`w-2 h-2 rounded-full ${statusDot} shrink-0`} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 truncate">{name}</div>
          <div className={`text-[11px] font-semibold ${statusText}`}>{statusLabel}</div>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-2" data-testid={`${testid}-body`}>
          {quotesForBiz.length === 0 ? (
            <div className="text-[11px] text-slate-500">No items for this business.</div>
          ) : (
            quotesForBiz.map(({ item, quote }, idx) => (
              <QuotePreviewLine key={idx} item={item} quote={quote} idx={idx} testid={`${testid}-quote-${idx}`} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function QuotePreviewLine({ item, quote, idx, testid }) {
  const hasQuote = !!quote;
  const qty = Number(item.quantity || 0);
  const unitPrice = hasQuote && quote.unit_price != null ? Number(quote.unit_price) : null;
  const discount = hasQuote ? Number(quote.discount || 0) : 0;
  const lineTotal = hasQuote ? Number(quote.price || 0) : 0;
  return (
    <div
      data-testid={testid}
      className={`rounded-xl p-2.5 text-xs border ${hasQuote ? "border-emerald-100 bg-emerald-50/60" : "border-slate-100 bg-slate-50"}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] font-bold text-slate-400 tabular-nums w-5 shrink-0">{idx + 1}.</span>
        <div className="min-w-0 flex-1 font-semibold text-slate-900 truncate">
          {item.product || "(image-only item)"}
        </div>
        <div className="text-[11px] text-slate-500 shrink-0 tabular-nums">
          {qty} {item.unit}
        </div>
      </div>
      {hasQuote ? (
        <div className="mt-1 pl-6 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
          {unitPrice != null && (
            <span className="tabular-nums">₹{unitPrice.toFixed(2)} / {item.unit}</span>
          )}
          {discount > 0 && <span className="text-amber-700">−{discount}% off</span>}
          <span className="tabular-nums font-semibold text-slate-900">= ₹{lineTotal.toFixed(2)}</span>
          {quote.note && <span className="italic text-slate-500 truncate">&ldquo;{quote.note}&rdquo;</span>}
        </div>
      ) : (
        <div className="mt-1 pl-6 flex items-center gap-1 text-[11px] text-slate-500">
          <Circle className="w-2.5 h-2.5" strokeWidth={2.4} /> Awaiting quotation
        </div>
      )}
    </div>
  );
}

// Keep the older static-preview default export API intact
export default EnquiryCard;

// Icon export placeholder — avoids "CheckCircle2 unused" lint if this file grows
export const __unused = CheckCircle2;
